import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@fronterahealth/frontera-ui-components";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@fronterahealth/frontera-ui-components/ui/field";
import { Input } from "@fronterahealth/frontera-ui-components/ui/input";

import { useUpdateProviderProfileMutation } from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

interface SigFields {
  professionalTitle: string;
  licenseNumber: string;
}

export const SignatureSettings: React.FC = () => {
  const { loginProviderDetailsQuery } = useAdminData();
  const meta = loginProviderDetailsQuery.data?.getLoginProvider?.providermetadata;
  const updateProviderProfile = useUpdateProviderProfileMutation({});

  const { register, handleSubmit, reset } = useForm<SigFields>({
    defaultValues: { professionalTitle: "", licenseNumber: "" },
  });

  useEffect(() => {
    if (!meta) return;
    reset({
      professionalTitle: meta.professionalTitle ?? "",
      licenseNumber: meta.licenseNumber ?? "",
    });
  }, [meta, reset]);

  const onSubmit = handleSubmit(async (data) => {
    const response = await updateProviderProfile.mutateAsync({
      providerData: {
        professionalTitle: data.professionalTitle,
        licenseNumber: data.licenseNumber,
      },
    });

    if (!response.updateProviderProfile?.status) {
      notifyError(response.updateProviderProfile?.message || "Failed to update signature details.");
      return;
    }

    notifySuccess("Signature details updated.");
    loginProviderDetailsQuery.refetch();
  });

  return (
    <form onSubmit={onSubmit} className="mx-auto mt-8 flex w-full max-w-2xl flex-col">
      <div className="border bg-white rounded-xl p-6">
        <FieldSet>
          <FieldLegend>Signature</FieldLegend>
          <FieldDescription>These appear on the signature line of every report you sign.</FieldDescription>

          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="professionalTitle">Professional Title</FieldLabel>
              <Input
                id="professionalTitle"
                placeholder="e.g. Clinical Psychologist"
                {...register("professionalTitle")}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="licenseNumber">License / Certification #</FieldLabel>
              <Input id="licenseNumber" placeholder="e.g. PSY 12345" {...register("licenseNumber")} />
            </Field>
          </FieldGroup>
        </FieldSet>

        <div className="mt-4 flex justify-end">
          <Button type="submit" variant="primary" text="Save" disabled={updateProviderProfile.isPending} />
        </div>
      </div>
    </form>
  );
};
